import { FileText } from 'lucide-react';
import { lazy } from 'react';
import { msg } from '@/lib/i18n';
import { registerInvalidation } from '@/lib/realtime';
import { registerEntityHref } from '@/ui';
import { registerEntityTab } from '../entityTabs';
import { defineModule } from '../types';
import { reportsKeys } from './api';
import { exportsRoute, reportRoute, reportsRoute, runRoute, screensRoute, shareRoute, sharesRoute, tvRoute } from './routes';

/**
 * Reports (IA §2 Manage › Reports): report builder, runs, schedules, shared links, TV screens,
 * exports — and the public /share/:token and /tv/:token pages (no sign-in, no shell).
 */
export const reportsModule = defineModule({
  key: 'reports',
  routes: [reportsRoute, sharesRoute, screensRoute, exportsRoute, reportRoute, runRoute],
  publicRoutes: [shareRoute, tvRoute],
  nav: [
    {
      key: 'reports',
      label: msg('Reports'),
      description: msg('Reports built from the same data as every screen: scheduled, shared by link, or on a TV screen.'),
      icon: FileText,
      to: '/reports',
      section: 'manage',
      order: 90,
      module: 'reports',
      permission: 'reports.report.read',
      match: ['/reports'],
      children: [
        { key: 'links', label: msg('Shared links'), to: '/reports/links', permission: 'reports.share.manage' },
        { key: 'screens', label: msg('TV screens'), to: '/reports/screens', permission: 'reports.share.manage' },
        { key: 'exports', label: msg('Exports'), to: '/reports/exports' },
      ],
    },
  ],
  setup() {
    registerEntityHref('report', (id) => `/reports/${id}`);
    // The report page is a workspace of tabs; other modules may add theirs (IA §1.2).
    registerEntityTab('report', {
      key: 'build',
      label: msg('Build'),
      order: 10,
      permission: 'reports.report.manage',
      component: lazy(() => import('./tabs/BuildTab')),
    });
    registerEntityTab('report', { key: 'preview', label: msg('Preview'), order: 20, component: lazy(() => import('./tabs/PreviewTab')) });
    registerEntityTab('report', { key: 'runs', label: msg('Runs'), order: 30, component: lazy(() => import('./tabs/RunsTab')) });
    registerEntityTab('report', { key: 'schedule', label: msg('Schedule'), order: 40, component: lazy(() => import('./tabs/ScheduleTab')) });
    registerEntityTab('report', {
      key: 'links',
      label: msg('Links'),
      order: 50,
      permission: 'reports.share.manage',
      component: lazy(() => import('./tabs/LinksTab')),
    });
    // Events about a report (created, edited, generated, sent, schedule changed) and its links.
    registerInvalidation('report', (n) => [
      reportsKeys.report(n.subject_id ?? ''),
      reportsKeys.runs(n.subject_id ?? ''),
      ['reports', 'list'],
      ['reports', 'schedule'],
    ]);
    registerInvalidation('report_share', () => [['reports', 'shares']]);
  },
});
